/**
 * Number Formatting Utilities
 *
 * Utility functions for formatting numbers, durations, and other numeric values
 * for the Personal Trainer App.
 */

/**
 * Format a number with commas for thousands separator
 */
export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

/**
 * Format duration in minutes to a human-readable string
 */
export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}m`;
};

/**
 * Format duration from seconds to a human-readable string
 */
export const formatDurationFromSeconds = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  return formatDuration(minutes);
};

/**
 * Format a percentage value
 */
export const formatPercentage = (
  value: number,
  decimals: number = 0
): string => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * Round a measured value for DISPLAY: at most `maxDecimals` places (default
 * 1), with trailing zeros trimmed — `77.1618 → "77.2"`, `77.5 → "77.5"`,
 * `80 → "80"`.
 *
 * Display-only. The API serves and accepts up to three decimals so unit
 * conversions round-trip losslessly; this is for what a person reads on a
 * chip or card — session weights, plan/workout form targets, body
 * measurements — where conversion noise would read as arithmetic.
 */
export const formatDisplayValue = (
  value: number,
  maxDecimals: number = 1
): string => String(Number(value.toFixed(maxDecimals)));

/**
 * Format a weight value with unit
 */
export const formatWeight = (
  weight: number,
  unit: 'kg' | 'lbs' = 'kg'
): string => {
  const formattedWeight = weight.toFixed(1);
  return `${formattedWeight} ${unit}`;
};

/**
 * Format a distance value with unit
 */
export const formatDistance = (
  distance: number,
  unit: 'km' | 'mi' = 'km'
): string => {
  const formattedDistance = distance.toFixed(2);
  return `${formattedDistance} ${unit}`;
};

/**
 * Format calories burned
 */
export const formatCalories = (calories: number): string => {
  return `${formatNumber(calories)} cal`;
};

/**
 * Format a streak count
 */
export const formatStreak = (days: number): string => {
  if (days === 0) {
    return 'No streak';
  } else if (days === 1) {
    return '1 day streak';
  } else {
    return `${days} day streak`;
  }
};

/**
 * Format workout count
 */
export const formatWorkoutCount = (count: number): string => {
  if (count === 0) {
    return 'No workouts';
  } else if (count === 1) {
    return '1 workout';
  } else {
    return `${formatNumber(count)} workouts`;
  }
};

/**
 * Format exercise count
 */
export const formatExerciseCount = (count: number): string => {
  if (count === 0) {
    return 'No exercises';
  } else if (count === 1) {
    return '1 exercise';
  } else {
    return `${formatNumber(count)} exercises`;
  }
};

/**
 * Format a range of numbers (e.g., "1-3", "5-10")
 */
export const formatRange = (min: number, max: number): string => {
  if (min === max) {
    return min.toString();
  }
  return `${min}-${max}`;
};

/**
 * Format a file size in bytes to human-readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format a rating value (e.g., "4.5/5")
 */
export const formatRating = (rating: number, maxRating: number = 5): string => {
  return `${rating.toFixed(1)}/${maxRating}`;
};

/**
 * Format a countdown timer (e.g., "2:30", "1:05:30")
 */
export const formatCountdown = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  } else {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
};

/**
 * The suffix that makes a number a position — `1` → `st`, `12` → `th`,
 * `22` → `nd`. The teens are the trap: 11, 12 and 13 take `th` whatever their
 * last digit says. For a layout that sets the suffix apart from the figure.
 */
export const ordinalSuffix = (n: number): string => {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return 'th';

  switch (n % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

/** A number as a position: 1st, 2nd, 3rd, 4th … 11th, 12th, 13th, 21st. */
export const formatOrdinal = (n: number): string => `${n}${ordinalSuffix(n)}`;
